import React, { useState, useEffect, useRef } from 'react'
import classnames from 'classnames'
import { Formik, Form, Field } from 'formik'
import API from '../../utils/API'

import { string, number, object, mixed, array } from 'yup'
import {
  Form as RBForm,
  Row,
  Col,
  Accordion,
  Card,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import {
  Button,
  useUserService,
  paymentType as paymentTypeConst,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import { ClearMonthAndReportValue } from './ClearMonthAndReportValue'
import SOWCopySelection from './SOWCopySelection'
import { SelectionField } from '../common'
import {
  // SingleInputField,
  SingleInputLabel,
  InputField,
  Checkbox,
  SingleDatePicker,
} from '../form'
import Labels from '../../constants/labels'
import { isLGScreen } from '../../utils/utility'
import { coachingPlanWIP } from '../../constants/common'
import {
  SetTotalSessionDuration,
  SetTotalSession,
} from './SetTotalSessionDuration'
import CoachingPlanDetailsModal from './CoachingPlanDetailsModal'
import PausingCurrentSOWModal from './PausingCurrentSOWModal'
import {
  formatDate,
  isValidDate,
  isDateBefore,
  getDateWithOffset,
} from '../../utils/date'
import { sowStatus, sowStatusLabel } from '@abroad/components/src/helper/common'
import SchedulingConditionRow from './SchedulingConditionRow'
import {
  getPlatformSchedulingDisabledMessage,
  getBulkPlatformSchedulingDisabledMessage,
  isPlatformSchedulingAvailable,
  canBulkAssignUsePlatformScheduling,
} from '../scheduling/schedulingPreconditions'
import {
  prepareFormValuesForSubmission,
  getConditionalSessionLabel,
  getCoachSchedulingFlags,
  getSowEmailPreviewDisabledReason,
  buildSowEmailPreviewPayload,
} from './schedulingHelpers'
import IncludeBioInMailField from './IncludeBioInMailField'
import SOWEmailPreviewModal from './SOWEmailPreviewModal'
import SOWEmailPreviewButton from './SOWEmailPreviewButton'
import { InformationIcon } from '../assetsComponents'

const labelProps = {
  column: isLGScreen ? true : false,
  sm: '2',
}

// Component to handle Conditional Scheduling based on totalSession
const ConditionalSchedulingHandler = ({
  values,
  setFieldValue,
  notificationService,
}) => {
  const prevTotalSession = useRef(values.totalSession)
  const prevIsEnabled = useRef(values.isConditionalSchedulingEnabled)
  const prevSowType = useRef(values.sowType)

  useEffect(() => {
    const totalSessions = Number(values.totalSession) || 0
    const isEnabled = values.isConditionalSchedulingEnabled
    const currentConditions = values.schedulingConditions || []
    const isQuestType = values.sowType === 'QUEST'

    // For QUEST type SOW, always keep conditional scheduling enabled
    if (isQuestType && !isEnabled) {
      setFieldValue('isConditionalSchedulingEnabled', true)
      prevIsEnabled.current = true
    }

    // For QUEST type SOW, show notification if user enters more than 3 sessions
    if (isQuestType && totalSessions > 3) {
      // Show notification if user just changed the value to more than 3
      if (
        prevTotalSession.current !== values.totalSession &&
        prevTotalSession.current !== undefined &&
        notificationService
      ) {
        notificationService.showNotification(
          'A maximum of 3 sessions can be taken in the experience type SOW.',
          'danger',
        )
      }
    }

    // If totalSession becomes > 3 for non-QUEST type, uncheck Conditional Scheduling
    if (!isQuestType && totalSessions > 3 && isEnabled) {
      setFieldValue('isConditionalSchedulingEnabled', false)
      // Clear scheduling conditions when disabled
      if (currentConditions.length > 0) {
        setFieldValue('schedulingConditions', [])
      }
      prevTotalSession.current = values.totalSession
      prevIsEnabled.current = false
      return
    }

    // Initialize or adjust schedulingConditions array when conditional scheduling is enabled
    if (isEnabled && totalSessions > 0 && totalSessions <= 3) {
      // Check if array needs to be initialized or resized
      if (
        !Array.isArray(currentConditions) ||
        currentConditions.length !== totalSessions
      ) {
        const newConditions = []
        for (let i = 0; i < totalSessions; i++) {
          // Preserve existing condition if it exists, otherwise create new one
          if (currentConditions[i]) {
            newConditions[i] = {
              ...currentConditions[i],
              sessionIndex: i + 1,
            }
          } else {
            newConditions[i] = {
              sessionIndex: i + 1,
              condition: '',
            }
          }
        }
        setFieldValue('schedulingConditions', newConditions)
      } else {
        // Ensure all items have correct sessionIndex
        const updatedConditions = currentConditions.map((condition, index) => ({
          ...condition,
          sessionIndex: index + 1,
        }))
        // Only update if sessionIndex values changed
        const needsUpdate = updatedConditions.some(
          (cond, idx) =>
            cond.sessionIndex !==
            (currentConditions[idx]?.sessionIndex || idx + 1),
        )
        if (needsUpdate) {
          setFieldValue('schedulingConditions', updatedConditions)
        }
      }
    } else if (!isEnabled && !isQuestType && currentConditions.length > 0) {
      // Clear scheduling conditions when disabled (only for non-QUEST type)
      setFieldValue('schedulingConditions', [])
    }

    prevTotalSession.current = values.totalSession
    prevIsEnabled.current = isEnabled
    prevSowType.current = values.sowType
  }, [
    values.totalSession,
    values.isConditionalSchedulingEnabled,
    values.sowType,
    setFieldValue,
    values.schedulingConditions,
    notificationService,
  ])

  return null
}

// Component to handle Quest type SOW default values
const QuestTypeDefaults = ({ values, setFieldValue, notificationService }) => {
  const prevSowType = useRef(values.sowType)
  const prevSowCopy = useRef(values.sowCopy)
  const prevTotalSession = useRef(values.totalSession)

  useEffect(() => {
    // Check if this is a copy operation (sowCopy was just set)
    const isCopyOperation =
      values.sowCopy &&
      Array.isArray(values.sowCopy) &&
      values.sowCopy.length > 0 &&
      (!prevSowCopy.current ||
        prevSowCopy.current.length === 0 ||
        prevSowCopy.current[0]?.value !== values.sowCopy[0]?.value)

    // When SOW type changes to QUEST, but only if it's NOT from a copy operation
    // If copying from an existing QUEST SOW, preserve the copied values instead of applying defaults
    if (
      values.sowType === 'QUEST' &&
      prevSowType.current !== 'QUEST' &&
      !isCopyOperation
    ) {
      // Set default values for Quest type (only when manually changing to QUEST)
      if (values.isPaymentRequired !== 'false') {
        setFieldValue('isPaymentRequired', 'false')
      }
      if (values.paymentCycle !== paymentTypeConst.ONETIME) {
        setFieldValue('paymentCycle', paymentTypeConst.ONETIME)
      }
      if (values.onetimeAmount !== 0) {
        setFieldValue('onetimeAmount', 0)
      }
      if (values.totalDurationOfContract !== '1') {
        setFieldValue('totalDurationOfContract', '1')
      }
      if (values.sessionPerCoachingCycle !== 2) {
        setFieldValue('sessionPerCoachingCycle', 2)
      }
      if (values.numberOfCoachingCycle !== 1) {
        setFieldValue('numberOfCoachingCycle', 1)
      }
      if (values.typeOfCoachingCycle !== 'Yearly') {
        setFieldValue('typeOfCoachingCycle', 'Yearly')
      }
      // Set default totalSession to 2 when switching to QUEST type (if not already set)
      const currentTotalSession = Number(values.totalSession) || 0
      if (currentTotalSession === 0 || currentTotalSession !== 2) {
        setFieldValue('totalSession', 2)
      }
      // Always enable conditional scheduling for QUEST type
      if (!values.isConditionalSchedulingEnabled) {
        setFieldValue('isConditionalSchedulingEnabled', true)
      }
      if (!values.includeGrowthSession) {
        setFieldValue('includeGrowthSession', true)
      }
      if (values.sessionDuration !== 60) {
        setFieldValue('sessionDuration', 60)
      }
      if (values.growthSessionDuration !== 60) {
        setFieldValue('growthSessionDuration', 60)
      }
      // Clear rates - creator will set
      if (values.coachingRate) {
        setFieldValue('coachingRate', '')
      }
      if (values.growthCoachingRate) {
        setFieldValue('growthCoachingRate', '')
      }
    }

    // For QUEST type, show notification if user enters more than 3 sessions
    if (values.sowType === 'QUEST') {
      const currentTotalSession = Number(values.totalSession) || 0
      // Show notification if user just changed the value to more than 3
      if (
        currentTotalSession > 3 &&
        prevTotalSession.current !== values.totalSession &&
        prevTotalSession.current !== undefined &&
        notificationService
      ) {
        notificationService.showNotification(
          'A maximum of 3 sessions can be taken in the experience type SOW.',
          'danger',
        )
      }
      // Always ensure conditional scheduling is enabled for QUEST type
      if (!values.isConditionalSchedulingEnabled) {
        setFieldValue('isConditionalSchedulingEnabled', true)
      }
    }

    // Update the refs
    prevSowType.current = values.sowType
    prevSowCopy.current = values.sowCopy
    prevTotalSession.current = values.totalSession
  }, [
    values.sowType,
    values.sowCopy,
    values.totalSession,
    values.isConditionalSchedulingEnabled,
    setFieldValue,
    values,
    notificationService,
  ])

  return null
}

// Component to handle ONE-TIME autofill for growth session fields
const GrowthSessionAutofill = ({ values, setFieldValue }) => {
  const prevIncludeGrowthSession = useRef(values.includeGrowthSession)

  // Track if autofill has already been done for each field
  const hasAutofilledDuration = useRef(false)
  const hasAutofilledRate = useRef(false)
  const hasAutofilledRateType = useRef(false)

  // Track previous values to detect actual changes
  const prevDuration = useRef(null)
  const prevRate = useRef(null)
  const prevRateType = useRef(null)

  const isEmptyValue = (value) =>
    value === undefined || value === null || value === ''

  useEffect(() => {
    // When checkbox changes from false to true
    if (values.includeGrowthSession && !prevIncludeGrowthSession.current) {
      // If Regular fields have values, autofill immediately
      // But DON'T mark as autofilled yet - only mark when user makes changes
      if (
        values.sessionDuration &&
        isEmptyValue(values.growthSessionDuration)
      ) {
        setFieldValue('growthSessionDuration', values.sessionDuration)
        prevDuration.current = values.sessionDuration
        // Mark as autofilled ONLY if user won't be changing it
        // (i.e., if they filled it before enabling checkbox)
        hasAutofilledDuration.current = true
      }
      if (values.coachingRate && isEmptyValue(values.growthCoachingRate)) {
        setFieldValue('growthCoachingRate', values.coachingRate)
        prevRate.current = values.coachingRate
        hasAutofilledRate.current = true
      }
      if (
        values.coachingRateType &&
        isEmptyValue(values.growthCoachingRateType)
      ) {
        setFieldValue('growthCoachingRateType', values.coachingRateType)
        prevRateType.current = values.coachingRateType
        // DON'T mark as autofilled - allow subsequent changes to trigger autofill
        // hasAutofilledRateType.current = true
      }
    }

    // Reset autofill flags when growth plan is disabled
    if (!values.includeGrowthSession && prevIncludeGrowthSession.current) {
      hasAutofilledDuration.current = false
      hasAutofilledRate.current = false
      hasAutofilledRateType.current = false
      prevDuration.current = null
      prevRate.current = null
      prevRateType.current = null
    }

    // Update the ref to current value
    prevIncludeGrowthSession.current = values.includeGrowthSession
  }, [
    values.includeGrowthSession,
    values.sessionDuration,
    values.growthSessionDuration,
    values.coachingRate,
    values.growthCoachingRate,
    values.coachingRateType,
    values.growthCoachingRateType,
    setFieldValue,
  ])

  // ONE-TIME autofill for Duration when user types (with delay)
  useEffect(() => {
    if (
      values.includeGrowthSession &&
      !hasAutofilledDuration.current &&
      values.sessionDuration &&
      values.sessionDuration !== prevDuration.current &&
      isEmptyValue(values.growthSessionDuration)
    ) {
      const timer = setTimeout(() => {
        if (!hasAutofilledDuration.current) {
          setFieldValue('growthSessionDuration', values.sessionDuration)
          hasAutofilledDuration.current = true
        }
      }, 500) // Wait 500ms after user stops typing

      return () => clearTimeout(timer)
    }
    prevDuration.current = values.sessionDuration
  }, [
    values.includeGrowthSession,
    values.sessionDuration,
    values.growthSessionDuration,
    setFieldValue,
  ])

  // ONE-TIME autofill for Rate when user types (with delay)
  useEffect(() => {
    if (
      values.includeGrowthSession &&
      !hasAutofilledRate.current &&
      values.coachingRate &&
      values.coachingRate !== prevRate.current &&
      isEmptyValue(values.growthCoachingRate)
    ) {
      const timer = setTimeout(() => {
        if (!hasAutofilledRate.current) {
          setFieldValue('growthCoachingRate', values.coachingRate)
          hasAutofilledRate.current = true
        }
      }, 500) // Wait 500ms after user stops typing

      return () => clearTimeout(timer)
    }
    prevRate.current = values.coachingRate
  }, [
    values.includeGrowthSession,
    values.coachingRate,
    values.growthCoachingRate,
    setFieldValue,
  ])

  // ONE-TIME autofill for Rate Type when user changes it
  useEffect(() => {
    if (
      values.includeGrowthSession &&
      !hasAutofilledRateType.current &&
      values.coachingRateType &&
      values.coachingRateType !== prevRateType.current &&
      isEmptyValue(values.growthCoachingRateType)
    ) {
      setFieldValue('growthCoachingRateType', values.coachingRateType)
      hasAutofilledRateType.current = true
    }
    prevRateType.current = values.coachingRateType
  }, [
    values.includeGrowthSession,
    values.coachingRateType,
    values.growthCoachingRateType,
    setFieldValue,
  ])

  return null
}

const CoachSchedulingHandler = ({
  values,
  setFieldValue,
  coaches,
  planId,
  coachSchedulingFlagsByCoachId = {},
  isBulkAssignSOWSection = false,
}) => {
  useEffect(() => {
    if (planId !== 'create' && !isBulkAssignSOWSection) {
      return
    }

    if (isBulkAssignSOWSection) {
      const bulkAssignCoachIds = values.bulkAssignCoachIds || []

      if (bulkAssignCoachIds.length === 0) {
        return
      }

      if (
        !canBulkAssignUsePlatformScheduling(
          bulkAssignCoachIds,
          coachSchedulingFlagsByCoachId,
        ) &&
        (values.isPlatformScheduling === 'true' ||
          values.isPlatformScheduling === true)
      ) {
        setFieldValue('isPlatformScheduling', 'false')
      }
      return
    }

    if (!values.sowCoachId) {
      return
    }

    const coachSchedulingFlags = getCoachSchedulingFlags(
      coaches,
      values.sowCoachId,
      coachSchedulingFlagsByCoachId,
    )

    if (
      !isPlatformSchedulingAvailable(coachSchedulingFlags) &&
      (values.isPlatformScheduling === 'true' ||
        values.isPlatformScheduling === true)
    ) {
      setFieldValue('isPlatformScheduling', 'false')
    }
  }, [
    values.sowCoachId,
    values.bulkAssignCoachIds,
    values.isPlatformScheduling,
    coaches,
    planId,
    setFieldValue,
    coachSchedulingFlagsByCoachId,
    isBulkAssignSOWSection,
  ])

  return null
}

// Component to handle Scheduling Type changes
const SchedulingTypeHandler = ({
  values,
  setFieldValue,
  notificationService,
}) => {
  const prevIsPlatformScheduling = useRef(
    values.isPlatformScheduling !== false &&
      values.isPlatformScheduling !== 'false',
  )

  useEffect(() => {
    const isPlatformScheduling =
      values.isPlatformScheduling === 'true' ||
      values.isPlatformScheduling === true ||
      (values.isPlatformScheduling !== false &&
        values.isPlatformScheduling !== 'false') // Default to true

    // When switching from Platform to Manual Scheduling
    if (
      (values.isPlatformScheduling === false ||
        values.isPlatformScheduling === 'false') &&
      prevIsPlatformScheduling.current
    ) {
      // Reset SOW type to REGULAR if it's QUEST
      if (values.sowType === 'QUEST') {
        setFieldValue('sowType', 'REGULAR')
        if (notificationService) {
          notificationService.showNotification(
            'Experience type SOW is only available with Platform Scheduling.',
            'danger',
          )
        }
        // Clear questId if set
        if (values.questId) {
          setFieldValue('questId', '')
        }
        // Reset all QUEST-specific default values back to initial/default values
        // Reset payment settings (QUEST sets isPaymentRequired to 'false')
        if (values.isPaymentRequired === 'false') {
          setFieldValue('isPaymentRequired', 'true')
        }
        // Reset payment cycle (QUEST sets to ONETIME)
        if (values.paymentCycle === paymentTypeConst.ONETIME) {
          setFieldValue('paymentCycle', paymentTypeConst.RECURRING)
        }
        // Reset onetime amount (QUEST sets to 0)
        if (values.onetimeAmount === 0 || values.onetimeAmount === '0') {
          setFieldValue('onetimeAmount', '')
        }
        // Reset coaching cycle settings (QUEST sets specific values)
        if (values.totalDurationOfContract === '1') {
          setFieldValue('totalDurationOfContract', '')
        }
        if (
          values.sessionPerCoachingCycle === 2 ||
          values.sessionPerCoachingCycle === '2'
        ) {
          setFieldValue('sessionPerCoachingCycle', '')
        }
        if (
          values.numberOfCoachingCycle === 1 ||
          values.numberOfCoachingCycle === '1'
        ) {
          setFieldValue('numberOfCoachingCycle', '')
        }
        if (values.typeOfCoachingCycle === 'Yearly') {
          setFieldValue('typeOfCoachingCycle', 'Monthly')
        }
        // Reset total session (QUEST sets to 2)
        if (values.totalSession === 2 || values.totalSession === '2') {
          setFieldValue('totalSession', '')
        }
        // Reset session durations (QUEST sets both to 60)
        if (values.sessionDuration === 60 || values.sessionDuration === '60') {
          setFieldValue('sessionDuration', '')
        }
        if (
          values.growthSessionDuration === 60 ||
          values.growthSessionDuration === '60'
        ) {
          setFieldValue('growthSessionDuration', '')
        }
        // Reset growth session (QUEST enables it by default)
        if (values.includeGrowthSession === true) {
          setFieldValue('includeGrowthSession', false)
        }
        // Clear growth coaching rates if they were set
        if (values.growthCoachingRate) {
          setFieldValue('growthCoachingRate', '')
        }
      } else {
        // Even if not QUEST, clear questId if set
        if (values.questId) {
          setFieldValue('questId', '')
        }
      }
      // Disable conditional scheduling
      if (values.isConditionalSchedulingEnabled) {
        setFieldValue('isConditionalSchedulingEnabled', false)
        if (values.schedulingConditions?.length > 0) {
          setFieldValue('schedulingConditions', [])
        }
      }
    }

    prevIsPlatformScheduling.current = isPlatformScheduling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.isPlatformScheduling,
    values.sowType,
    values.questId,
    values.isConditionalSchedulingEnabled,
    setFieldValue,
    notificationService,
  ])

  return null
}

const { SOWBillEvery } = Labels
const indiSOWSchema = object().shape({
  planName: string().required('PlanName is a required field').max(160),
  // Billing
  paymentCycle: string().required(),

  invoiceEvery: mixed().when(['paymentCycle'], {
    is: (paymentCycle) => paymentCycle === paymentTypeConst.RECURRING,
    then: number().required().oneOf(SOWBillEvery),
    otherwise: () => mixed().notRequired(),
  }),
  numberOfBillingCycle: number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value,
    )
    .nullable()
    .when('paymentCycle', {
      is: (paymentCycle) => paymentCycle === paymentTypeConst.RECURRING,
      then: (schema) =>
        schema.min(1, 'Minimum is 1').max(1000, 'Maximum is 1000').nullable(),
      otherwise: (schema) => schema.notRequired(),
    }),
  subscriptionAmount: number()
    .transform((value, originalValue) => {
      // Convert empty string or null to undefined
      return originalValue === '' || originalValue === null ? undefined : value
    })
    .nullable()
    .test(
      'subscription-amount',
      'Invalid subscription amount',
      function (value) {
        const { paymentCycle, isPaymentRequired } = this.parent

        // Skip validation if paymentCycle is not RECURRING
        if (paymentCycle !== paymentTypeConst.RECURRING) {
          return true
        }

        // Only validate if paymentCycle is RECURRING
        if (value === undefined || value === null || value === '') {
          return this.createError({
            message: 'Subscription amount is required',
          })
        }

        // Allow 0 when payment is not required
        if (isPaymentRequired === 'false' && value >= 0) {
          if (value > 10000) {
            return this.createError({
              message: 'The subscription amount cannot exceed $10000',
            })
          }
          return true
        }

        // When payment is required, minimum is 1
        if (value < 1) {
          return this.createError({
            message: 'Please enter a subscription amount of $1 or more',
          })
        }

        if (value > 10000) {
          return this.createError({
            message: 'The subscription amount cannot exceed $10000',
          })
        }

        return true
      },
    ),
  // Coaching

  onetimeAmount: number()
    .transform((value, originalValue) => {
      return originalValue === '' || originalValue === null ? undefined : value
    })
    .when(['paymentCycle', 'isPaymentRequired'], {
      is: (paymentCycle, isPaymentRequired) => {
        return paymentCycle === paymentTypeConst.ONETIME
      },
      then: (schema) =>
        schema
          .required()
          .test('onetime-amount', 'Invalid one time amount', function (value) {
            const { isPaymentRequired } = this.parent

            // Allow 0 when payment is not required
            if (isPaymentRequired === 'false' && value >= 0 && value <= 10000) {
              return true
            }

            // When payment is required, minimum is 1
            if (value < 1) {
              return this.createError({
                message: 'Please enter a onetime amount of $1 or more',
              })
            }

            if (value > 10000) {
              return this.createError({
                message: 'The onetime amount cannot exceed $10000',
              })
            }

            return true
          }),
      otherwise: (schema) => schema.notRequired(),
    }),

  upfrontFee: number()
    // .transform((value) => (value === '' || value === null ? undefined : value))
    .when('paymentCycle', {
      is: (paymentCycle) => paymentCycle === paymentTypeConst.RECURRING,
      then: (schema) => schema.required().min(0).max(1000),
      otherwise: (schema) => schema.notRequired(),
    }),

  totalSession: number()
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value
    })
    .when(['numberOfBillingCycle', 'totalDurationOfContract', 'sowType'], {
      is: (billingCycle, contractDuration, sowType) =>
        (billingCycle != null && billingCycle !== '') ||
        (contractDuration != null &&
          contractDuration !== '' &&
          contractDuration !== 'Indefinite'),
      then: (schema) =>
        schema
          .required('Total session is a required field')
          .min(1)
          .when('sowType', {
            is: 'QUEST',
            then: (s) =>
              s.max(
                3,
                'A maximum of 3 sessions can be taken in the experience type SOW.',
              ),
            otherwise: (s) => s.max(999),
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
  sessionPerCoachingCycle: number().required().min(1).max(36),
  numberOfCoachingCycle: number().required().oneOf(SOWBillEvery),
  sessionDuration: number().when('isOld', {
    is: true,
    then: number().notRequired(),
    otherwise: () => number().required().min(15).max(600),
  }),

  coachingRate: number()
    .required('CoachRate is a required field')
    .min(0)
    .max(1000),
  growthCoachingRate: number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value,
    )
    .nullable()
    .when('includeGrowthSession', {
      is: true,
      then: (schema) =>
        schema.required('CoachRate is a required field').min(0).max(1000),
      otherwise: (schema) => schema.notRequired(),
    }),

  growthSessionDuration: number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value,
    )
    .nullable()
    .when('includeGrowthSession', {
      is: true,
      then: (schema) => schema.required().min(1).max(1000),
      otherwise: (schema) => schema.notRequired(),
    }),
  clientTimeZone: string().required(),
  planStartDate: mixed()
    .required('Plan Starts date is a required field')
    .test('valid-date', 'Please enter valid date!', function (value) {
      if (!value) {
        return this.createError({
          message: 'Plan Starts date is a required field',
        })
      }
      return (
        isValidDate(value, 'MM/DD/YYYY') ||
        this.createError({ message: 'Please enter valid date!' })
      )
    }),
  includeGrowthSession: mixed().notRequired(),
  sowType: string().required('SOW Type is a required field'),
  questId: string()
    .when(['sowType', 'isPlatformScheduling'], {
      is: (sowType, isPlatformScheduling) =>
        sowType === 'QUEST' &&
        (isPlatformScheduling === true ||
          isPlatformScheduling === 'true' ||
          isPlatformScheduling !== false),
      then: (schema) => schema.required('Experience is a required field'),
      otherwise: (schema) => schema.notRequired(),
    })
    .test(
      'quest-id-platform-scheduling',
      'Experience is only available with Platform Scheduling',
      function (value) {
        const { isPlatformScheduling } = this.parent
        if (
          value &&
          (isPlatformScheduling === false || isPlatformScheduling === 'false')
        ) {
          return this.createError({
            message: 'Experience is only available with Platform Scheduling',
          })
        }
        return true
      },
    ),
  isPlatformScheduling: mixed().notRequired(),
  isConditionalSchedulingEnabled: mixed().notRequired(),
  schedulingConditions: array().when(
    ['isConditionalSchedulingEnabled', 'totalSession'],
    {
      is: (isEnabled, totalSession) => {
        return (
          isEnabled &&
          totalSession &&
          Number(totalSession) > 0 &&
          Number(totalSession) <= 3
        )
      },
      then: (schema) =>
        schema
          .required('Scheduling conditions are required fields')
          .min(1, 'At least one scheduling condition is a required field')
          .test(
            'scheduling-conditions-length',
            'All sessions must have scheduling conditions',
            function (value) {
              const { totalSession } = this.parent
              const totalSessions = Number(totalSession) || 0

              if (!Array.isArray(value) || value.length !== totalSessions) {
                return this.createError({
                  message: `Please configure scheduling conditions for all ${totalSessions} session(s)`,
                })
              }

              return true
            },
          )
          .of(
            object().shape({
              sessionIndex: number().required(),
              condition: string()
                .required('Condition is a required field')
                .oneOf(
                  ['on_or_before', 'on_or_after', 'between'],
                  'Invalid condition',
                ),
              primaryDate: string()
                .required('Start date is a required field')
                .test(
                  'valid-date',
                  'Please enter valid start date',
                  function (value) {
                    if (!value) return false
                    return isValidDate(value, 'MM/DD/YYYY')
                  },
                ),
              secondaryDate: string().when('condition', {
                is: 'between',
                then: (schema) =>
                  schema
                    .required('End date is a required field')
                    .test(
                      'valid-date',
                      'Please enter valid end date',
                      function (value) {
                        if (!value) return false
                        if (!isValidDate(value, 'MM/DD/YYYY')) return false

                        // Validate that end date is after start date
                        if (
                          isValidDate(this.parent.primaryDate, 'MM/DD/YYYY') &&
                          isDateBefore(
                            value,
                            this.parent.primaryDate,
                            'MM/DD/YYYY',
                          )
                        ) {
                          return this.createError({
                            message: 'End date must be after start date',
                          })
                        }
                        return true
                      },
                    ),
                otherwise: (schema) => schema.notRequired(),
              }),
            }),
          ),
      otherwise: (schema) => schema.notRequired(),
    },
  ),
})

const IndividualSOWForm = ({
  // isStaffCoachSOW = false, // todo: remove staff coach condition
  initialValues,
  setInitialValues,
  onSubmit,
  viewOnlyField,
  isPlanTerminated,
  isUpdatePlanLoading,
  coaches = [],
  isBulkAssignSOWSection = false,
  updateFormValuesHandle = () => {},
  onBackToPairing = () => {},
  // Bulk assign only: the first selected client and that client's coach, used for the email preview
  previewSample = null,
}) => {
  const { planId, clientId } = useParams()
  const Error = useErrorService()
  const Notification = useNotificationService()

  const { user } = useUserService()
  const [showModal, setShowModal] = useState(false)
  const [showEmailPreview, setShowEmailPreview] = useState(false)
  const [pausingCurrentSOWPopup, setPausingCurrentSOWPopup] = useState(false)
  const [isPaymentReminderLoading, setIsPaymentReminderLoading] =
    useState(false)
  const [questList, setQuestList] = useState([])
  const [isLoadingQuests, setIsLoadingQuests] = useState(false)
  const isSowStatusDraft = initialValues?.sowStatus === sowStatus.DRAFT
  const { RECURRING: recurringPayment, ONETIME: oneTimePayment } =
    paymentTypeConst

  const isNotBulkSOWNorCreate = !isBulkAssignSOWSection && planId !== 'create'

  // Fetch quest list when clientId is available or for bulk assign
  useEffect(() => {
    const fetchQuestList = async () => {
      // For bulk assign, fetch all quests without clientId
      if (isBulkAssignSOWSection) {
        try {
          setIsLoadingQuests(true)
          const { data } = await API.AdminAPI.quests.getQuestList({
            page: 1,
            limit: 500,
            isArchived: false,
          })
          // Transform the response to match SelectionField format
          const formattedQuests = (data || []).map((quest) => ({
            value: quest.questId || quest._id,
            label: quest.name || quest.title,
          }))
          setQuestList(formattedQuests)
        } catch (error) {
          Error.showError(error)
          setQuestList([])
        } finally {
          setIsLoadingQuests(false)
        }
        return
      }

      // For individual SOW, fetch quests for specific client
      if (!clientId || clientId === 'create') {
        return
      }

      try {
        setIsLoadingQuests(true)
        const { data } = await API.AdminAPI.quests.getClientQuestList(clientId)
        // Transform the response to match SelectionField format
        const formattedQuests = (data || []).map((quest) => ({
          value: quest.questId,
          label: quest.name || quest.title,
        }))
        setQuestList(formattedQuests)
      } catch (error) {
        Error.showError(error)
        setQuestList([])
      } finally {
        setIsLoadingQuests(false)
      }
    }

    fetchQuestList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, isBulkAssignSOWSection])

  const sendPaymentReminder = async () => {
    if (!planId || planId === 'create') {
      return
    }

    try {
      setIsPaymentReminderLoading(true)
      const { data } = await API.AdminAPI.coachPlan.sendPaymentReminder(planId)
      Notification.showNotification(data?.message, 'success')
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsPaymentReminderLoading(false)
    }
  }

  return (
    <>
      {viewOnlyField && (
        <div className='d-flex justify-content-between mb-3'>
          <h2 className='font-22 font-semibold mb-0'>
            SOW Status:{' '}
            {sowStatusLabel.find(
              (item) => item.value === initialValues.sowStatus,
            )?.label || ''}
            {initialValues?.setToBeTerminated && (
              <span className='text-red-800'>
                (*Set to be terminated on{' '}
                {formatDate(initialValues?.terminatedOn)})
              </span>
            )}
          </h2>
          {(initialValues.sowStatus === sowStatus.WAITING_FOR_PAYMENT ||
            initialValues.sowStatus === sowStatus.PAYMENT_FAILED) &&
            user?.isAdmin && (
              <Button
                type='button'
                variant='saffron'
                size='md'
                className='line-height-normal text-uppercase border-radius-3'
                onClick={sendPaymentReminder}
                disabled={isPaymentReminderLoading}>
                {isPaymentReminderLoading
                  ? 'Sending...'
                  : 'Send Payment Reminder'}
              </Button>
            )}
        </div>
      )}
      <Formik
        displayName='sow-form'
        validationSchema={indiSOWSchema}
        initialValues={{
          ...initialValues,
          sowType: initialValues?.sowType || 'REGULAR',
          isPlatformScheduling:
            initialValues?.isPlatformScheduling !== undefined &&
            initialValues?.isPlatformScheduling !== null
              ? initialValues.isPlatformScheduling === true ||
                initialValues.isPlatformScheduling === 'true'
                ? 'true'
                : 'false'
              : 'false',
        }}
        enableReinitialize={true}
        validateOnMount={!isBulkAssignSOWSection}
        onSubmit={async (values, actions) => {
          try {
            // Prepare form values using helper function
            const formValues = prepareFormValuesForSubmission(values)
            // If validation passes, continue as before
            if (!isBulkAssignSOWSection && planId) {
              if (formValues?.isPaymentRequired !== 'false') {
                setShowModal(true)
              } else {
                await onSubmit(formValues, actions)
              }
            } else if (isBulkAssignSOWSection) {
              await onSubmit(formValues, actions)
            } else {
              if (formValues.sowStatus === sowStatus.Draft) {
                setShowModal(true)
              } else {
                await onSubmit(formValues, actions)
              }
            }
          } catch (error) {
            Error.showError(error)
            if (actions && actions.setSubmitting) {
              actions.setSubmitting(false)
            }
          }
        }}>
        {({
          values,
          handleSubmit,
          initialValues,
          validateForm,
          setFieldValue,
          errors,
          touched,
          setFieldTouched,
        }) => {
          const emailPreviewIds = isBulkAssignSOWSection
            ? previewSample || {}
            : { clientId, coachId: values.sowCoachId }
          const showEmailPreviewButton =
            planId === 'create' || isBulkAssignSOWSection
          const emailPreviewDisabledReason = getSowEmailPreviewDisabledReason({
            values,
            ...emailPreviewIds,
          })
          const emailPreviewPayload = emailPreviewDisabledReason
            ? null
            : buildSowEmailPreviewPayload({ values, ...emailPreviewIds })
          // The Regular email is only sent when the coach has a video call link
          const emailPreviewCoachFlags = getCoachSchedulingFlags(
            coaches,
            emailPreviewIds.coachId,
            initialValues?.coachSchedulingFlagsByCoachId,
          )
          const isCoachMissingVideoCallLink =
            values.sowType !== 'QUEST' &&
            emailPreviewCoachFlags.hasVideoCallLink === false

          return (
            <>
              <SchedulingTypeHandler
                values={values}
                setFieldValue={setFieldValue}
                notificationService={Notification}
              />
              <CoachSchedulingHandler
                values={values}
                setFieldValue={setFieldValue}
                coaches={coaches}
                planId={planId}
                isBulkAssignSOWSection={isBulkAssignSOWSection}
                coachSchedulingFlagsByCoachId={
                  initialValues?.coachSchedulingFlagsByCoachId
                }
              />
              <QuestTypeDefaults
                values={values}
                setFieldValue={setFieldValue}
                notificationService={Notification}
              />
              <ConditionalSchedulingHandler
                values={values}
                setFieldValue={setFieldValue}
                notificationService={Notification}
              />
              <GrowthSessionAutofill
                values={values}
                setFieldValue={setFieldValue}
              />
              <RBForm
                onSubmit={handleSubmit}
                as={Form}
                className='profile promo-accordion'>
                <Accordion defaultActiveKey={'1'} key={'1'}>
                  <Card className='mb-3'>
                    <Accordion.Toggle
                      as={Card.Header}
                      eventKey={'1'}
                      className={classnames(
                        'border-0 cursor-pointer font-18 font-semibold px-3',
                      )}>
                      SOW Details
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey={'1'}>
                      <Card.Body className='px-3 d-flex flex-column'>
                        <Row className='mb-1 px-2' xs={1} sm={1} md={1} lg={2}>
                          <Col className='col-lg-2 text-lg-right text-left'>
                            <RBForm.Label>Scheduling Type</RBForm.Label>
                          </Col>
                          <Col className='d-flex col-lg-10 flex-column text-left align-items-start pl-2'>
                            {(() => {
                              const bulkAssignCoachIds =
                                values.bulkAssignCoachIds ||
                                initialValues?.bulkAssignCoachIds ||
                                []
                              const coachSchedulingFlags =
                                isBulkAssignSOWSection
                                  ? null
                                  : getCoachSchedulingFlags(
                                      coaches,
                                      values.sowCoachId,
                                      initialValues?.coachSchedulingFlagsByCoachId,
                                    )
                              const canUsePlatformScheduling =
                                isBulkAssignSOWSection
                                  ? canBulkAssignUsePlatformScheduling(
                                      bulkAssignCoachIds,
                                      initialValues?.coachSchedulingFlagsByCoachId,
                                    )
                                  : planId !== 'create' ||
                                    isPlatformSchedulingAvailable(
                                      coachSchedulingFlags,
                                    )
                              const platformSchedulingDisabledMessage =
                                isBulkAssignSOWSection
                                  ? getBulkPlatformSchedulingDisabledMessage(
                                      bulkAssignCoachIds,
                                      coaches,
                                      initialValues?.coachSchedulingFlagsByCoachId,
                                    )
                                  : getPlatformSchedulingDisabledMessage(
                                      coachSchedulingFlags,
                                    )
                              const isSchedulingFieldDisabled =
                                isSowStatusDraft || coachingPlanWIP
                                  ? viewOnlyField && !user?.isAdmin
                                  : viewOnlyField || showModal

                              const isPlatformSchedulingUnavailable =
                                (planId === 'create' ||
                                  isBulkAssignSOWSection) &&
                                !canUsePlatformScheduling

                              return (
                                <>
                                  <label
                                    className={classnames(
                                      'text-left d-flex align-items-center',
                                      {
                                        'cursor-disabled text-muted':
                                          isPlatformSchedulingUnavailable,
                                      },
                                    )}>
                                    <Field
                                      type='radio'
                                      name='isPlatformScheduling'
                                      value='true'>
                                      {({ field }) => {
                                        const isPlatformSchedulingDisabled =
                                          isSchedulingFieldDisabled ||
                                          isPlatformSchedulingUnavailable

                                        return (
                                          <input
                                            {...field}
                                            type='radio'
                                            value='true'
                                            className={classnames('mr-2', {
                                              'cursor-disabled':
                                                isPlatformSchedulingUnavailable,
                                            })}
                                            style={{
                                              cursor:
                                                isPlatformSchedulingUnavailable
                                                  ? 'not-allowed'
                                                  : 'pointer',
                                              opacity:
                                                isPlatformSchedulingUnavailable
                                                  ? 0.6
                                                  : 1,
                                            }}
                                            disabled={
                                              isPlatformSchedulingDisabled
                                            }
                                            onChange={(e) => {
                                              if (
                                                isPlatformSchedulingUnavailable
                                              ) {
                                                e.preventDefault()
                                                return
                                              }
                                              field.onChange(e)
                                            }}
                                            onClick={(e) => {
                                              if (
                                                isPlatformSchedulingUnavailable
                                              ) {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                return false
                                              }
                                            }}
                                          />
                                        )
                                      }}
                                    </Field>
                                    <span
                                      className={classnames(
                                        'font-weight-bold',
                                        {
                                          'text-muted':
                                            isPlatformSchedulingUnavailable,
                                        },
                                      )}>
                                      Platform Scheduling
                                    </span>
                                    {isPlatformSchedulingUnavailable &&
                                      platformSchedulingDisabledMessage && (
                                        <OverlayTrigger
                                          placement='top'
                                          overlay={
                                            <Tooltip className='font-open-sans font-16 font-semibold leading-7 text-center header-tooltip inviteonly-content-text modal-tooltip'>
                                              {
                                                platformSchedulingDisabledMessage
                                              }
                                            </Tooltip>
                                          }>
                                          <span className='d-inline-flex align-items-center ml-2'>
                                            <InformationIcon iconClasses='align-self-center' />
                                          </span>
                                        </OverlayTrigger>
                                      )}
                                  </label>
                                  <label className='text-left'>
                                    <Field
                                      type='radio'
                                      name='isPlatformScheduling'
                                      value='false'
                                      className='mr-2'
                                      disabled={isSchedulingFieldDisabled}
                                    />
                                    <span className='font-weight-bold'>
                                      Manual Scheduling
                                    </span>
                                  </label>
                                </>
                              )
                            })()}
                          </Col>
                        </Row>
                        {(planId === 'create' || isBulkAssignSOWSection) && (
                          <SOWCopySelection
                            name='sowCopy'
                            placeholder='SOW Copy From...'
                            label='Copy From'
                            isDiscardLabelPadding={!isBulkAssignSOWSection}
                            isInsidePopup={isBulkAssignSOWSection}
                            disabled={
                              coachingPlanWIP
                                ? viewOnlyField && !user?.isAdmin
                                : viewOnlyField || showModal
                            }
                          />
                        )}
                        {!isBulkAssignSOWSection && (
                          <SelectionField
                            label='Coach Name'
                            labelProps={labelProps}
                            name='sowCoachId'
                            size='lg'
                            options={coaches || []}
                            getValueAsOption={true}
                            optionplaceholder='Select Coach'
                            sm='10'
                            className={
                              planId !== 'create'
                                ? 'cursor-disabled'
                                : 'cursor-pointer'
                            }
                            disabled={
                              isSowStatusDraft || coachingPlanWIP
                                ? viewOnlyField && !user?.isAdmin
                                : viewOnlyField || showModal
                            }
                          />
                        )}
                        {(planId === 'create' || isSowStatusDraft) && (
                          <InputField
                            label='Plan Name'
                            labelProps={labelProps}
                            name='planName'
                            type='text'
                            size='lg'
                            placeholder='Plan Name'
                            disabled={
                              isSowStatusDraft || coachingPlanWIP
                                ? viewOnlyField && !user?.isAdmin
                                : viewOnlyField || showModal
                            }
                          />
                        )}
                        <Row className='mb-1 px-2' xs={1} sm={1} md={1} lg={2}>
                          <Col className='col-lg-2 text-lg-right text-left'>
                            <RBForm.Label>SOW Type</RBForm.Label>
                          </Col>
                          <Col className='d-flex col-lg-10 flex-column text-left align-items-start pl-2'>
                            <label>
                              <Field
                                type='radio'
                                name='sowType'
                                value='REGULAR'
                                className='mr-2'
                                disabled={
                                  isSowStatusDraft || coachingPlanWIP
                                    ? viewOnlyField && !user?.isAdmin
                                    : viewOnlyField || showModal
                                }
                              />
                              <span className='font-weight-bold'>REGULAR</span>
                            </label>
                            <label
                              className={classnames(
                                'text-left d-flex align-items-center',
                                {
                                  'cursor-disabled text-muted':
                                    values.isPlatformScheduling === false ||
                                    values.isPlatformScheduling === 'false',
                                },
                              )}>
                              <Field type='radio' name='sowType' value='QUEST'>
                                {({ field }) => {
                                  const isManualScheduling =
                                    values.isPlatformScheduling === false ||
                                    values.isPlatformScheduling === 'false'
                                  const isDisabled =
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField ||
                                        showModal ||
                                        isManualScheduling

                                  return (
                                    <input
                                      {...field}
                                      type='radio'
                                      value='QUEST'
                                      className={classnames('mr-2', {
                                        'cursor-disabled': isManualScheduling,
                                      })}
                                      style={{
                                        cursor: isManualScheduling
                                          ? 'not-allowed'
                                          : 'pointer',
                                        opacity: isManualScheduling ? 0.6 : 1,
                                      }}
                                      disabled={isDisabled}
                                      onChange={(e) => {
                                        // Prevent change if Manual Scheduling is selected
                                        if (isManualScheduling) {
                                          e.preventDefault()
                                          return
                                        }
                                        field.onChange(e)
                                      }}
                                      onClick={(e) => {
                                        // Prevent click if Manual Scheduling is selected
                                        if (isManualScheduling) {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          return false
                                        }
                                      }}
                                    />
                                  )
                                }}
                              </Field>
                              <span
                                className={classnames('font-weight-bold', {
                                  'text-muted':
                                    values.isPlatformScheduling === false ||
                                    values.isPlatformScheduling === 'false',
                                })}>
                                Experience
                              </span>
                              {(values.isPlatformScheduling === false ||
                                values.isPlatformScheduling === 'false') && (
                                <OverlayTrigger
                                  placement='top'
                                  overlay={
                                    <Tooltip className='font-open-sans font-16 font-semibold leading-7 text-center header-tooltip inviteonly-content-text modal-tooltip'>
                                      Experience type SOW is only available with
                                      Platform Scheduling.
                                    </Tooltip>
                                  }>
                                  <span className='d-inline-flex align-items-center ml-2'>
                                    <InformationIcon iconClasses='align-self-center' />
                                  </span>
                                </OverlayTrigger>
                              )}
                            </label>
                          </Col>
                        </Row>
                        {values.sowType === 'QUEST' &&
                          (clientId || isBulkAssignSOWSection) && (
                            <>
                              {viewOnlyField && initialValues?.questName ? (
                                <InputField
                                  label='Experience'
                                  labelProps={labelProps}
                                  name='questId'
                                  type='text'
                                  size='lg'
                                  value={initialValues?.questName}
                                  sm='10'
                                  disabled={true}
                                  removeFormikField={true}
                                />
                              ) : !isLoadingQuests &&
                                questList.length === 0 &&
                                !isBulkAssignSOWSection ? (
                                <InputField
                                  label='Experience'
                                  labelProps={labelProps}
                                  name='questId'
                                  type='text'
                                  size='lg'
                                  placeholder="This user didn't participate in any experience"
                                  sm='10'
                                  disabled={true}
                                  removeFormikField={true}
                                />
                              ) : (
                                <SelectionField
                                  label='Experience'
                                  labelProps={labelProps}
                                  name='questId'
                                  size='lg'
                                  options={questList || []}
                                  getValueAsOption={true}
                                  optionplaceholder={
                                    isLoadingQuests
                                      ? 'Loading Experiences...'
                                      : 'Select Experience'
                                  }
                                  sm='10'
                                  disabled={
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField ||
                                        showModal ||
                                        isLoadingQuests
                                  }
                                />
                              )}
                            </>
                          )}
                        {(planId === 'create' || isBulkAssignSOWSection) && (
                          <IncludeBioInMailField
                            coaches={coaches}
                            coachSchedulingFlagsByCoachId={
                              initialValues?.coachSchedulingFlagsByCoachId
                            }
                            planId={planId}
                            isBulkAssignSOWSection={isBulkAssignSOWSection}
                            disabled={
                              isSowStatusDraft || coachingPlanWIP
                                ? viewOnlyField && !user?.isAdmin
                                : viewOnlyField || showModal
                            }
                          />
                        )}
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                </Accordion>
                {user?.isAdmin && (
                  <Accordion defaultActiveKey={'2'} key={'2'}>
                    <Card className='mb-3'>
                      <Accordion.Toggle
                        as={Card.Header}
                        eventKey={'2'}
                        className={classnames(
                          'border-0 cursor-pointer font-18 font-semibold px-3',
                        )}>
                        Billing
                      </Accordion.Toggle>
                      <Accordion.Collapse eventKey={'2'}>
                        <Card.Body className='px-3 d-flex flex-column'>
                          <Row
                            className='mb-1 px-2'
                            xs={1}
                            sm={1}
                            md={1}
                            lg={2}>
                            <Col className='col-lg-2 text-lg-right text-left'>
                              {' '}
                              <RBForm.Label>Payment Required?</RBForm.Label>
                            </Col>

                            <Col className='d-flex col-lg-10 flex-column text-left align-items-start pl-2'>
                              <label>
                                <Field
                                  type='radio'
                                  name='isPaymentRequired'
                                  value='true'
                                  className='mr-2'
                                  disabled={
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField || showModal
                                  }
                                  defaultActiveKey={true}
                                />
                                <sapn className='font-weight-bold'>YES</sapn>{' '}
                                (The link will be sent to the client)
                              </label>
                              <label className='text-left'>
                                <Field
                                  type='radio'
                                  name='isPaymentRequired'
                                  value='false'
                                  className='mr-2'
                                  disabled={
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField || showModal
                                  }
                                />
                                <sapn className='font-weight-bold'>NO</sapn>{' '}
                                (You can still add payment if needed for
                                internal use/Reference)
                              </label>
                            </Col>
                          </Row>

                          <>
                            {(user.isAdmin || !viewOnlyField) && (
                              <SelectionField
                                label='PAYMENT TYPE'
                                labelProps={labelProps}
                                name='paymentCycle'
                                options={Labels.paymentCycle}
                                disabled={
                                  isSowStatusDraft || coachingPlanWIP
                                    ? viewOnlyField && !user?.isAdmin
                                    : viewOnlyField || showModal
                                }
                                size='lg'
                                getKeyAsOption={true}
                                className={classnames({
                                  'bg-white': isBulkAssignSOWSection,
                                })}
                              />
                            )}
                            {(user.isAdmin || !viewOnlyField) &&
                              values.paymentCycle === recurringPayment && (
                                <SelectionField
                                  label={
                                    values.paymentCycle === recurringPayment &&
                                    'Bill Every (Month(s))'
                                  }
                                  labelProps={labelProps}
                                  name='invoiceEvery'
                                  size='lg'
                                  options={SOWBillEvery.map((e) => ({
                                    key: e,
                                    value: e,
                                  }))}
                                  getKeyAsOption={true}
                                  // options={Labels.coachingPlanStatus}
                                  disabled={
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField || showModal
                                  }
                                  className={classnames({
                                    'd-none m-0':
                                      values.paymentCycle !== recurringPayment,
                                  })}
                                />
                              )}
                            {(user.isAdmin || !viewOnlyField) &&
                              values.paymentCycle === recurringPayment && (
                                <InputField
                                  label={
                                    values.paymentCycle === recurringPayment &&
                                    'Subscription Amount(USD)'
                                  }
                                  labelProps={labelProps}
                                  name='subscriptionAmount'
                                  type='number'
                                  size='lg'
                                  placeholder='Enter Subscription Fee'
                                  sm='10'
                                  getKeyAsOption={true}
                                  className={classnames({
                                    'd-none m-0':
                                      values.paymentCycle !== recurringPayment,
                                  })}
                                  disabled={
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField || showModal
                                  }
                                />
                              )}
                            {(user.isAdmin || !viewOnlyField) && (
                              <InputField
                                label={
                                  values.paymentCycle === recurringPayment
                                    ? 'UPFRONT/SETUP FEE(USD)'
                                    : 'ONE TIME FEE(USD)'
                                }
                                labelProps={labelProps}
                                name={
                                  values.paymentCycle === recurringPayment
                                    ? 'upfrontFee'
                                    : 'onetimeAmount'
                                }
                                type='number'
                                size='lg'
                                placeholder={
                                  values.paymentCycle !== recurringPayment
                                    ? 'Enter One Time Fee'
                                    : 'Enter Upfront Fee'
                                }
                                sm='10'
                                disabled={
                                  isSowStatusDraft || coachingPlanWIP
                                    ? viewOnlyField && !user?.isAdmin
                                    : viewOnlyField || showModal
                                }
                              />
                            )}
                            {(user.isAdmin || !viewOnlyField) &&
                              values.paymentCycle === recurringPayment && (
                                <InputField
                                  label={
                                    values.paymentCycle === recurringPayment &&
                                    'Number of Billing Cycle'
                                  }
                                  labelProps={labelProps}
                                  name='numberOfBillingCycle'
                                  type='number'
                                  size='lg'
                                  placeholder='Indefinite'
                                  sm='10'
                                  className={classnames({
                                    'd-none m-0':
                                      values.paymentCycle !== recurringPayment,
                                  })}
                                  disabled={
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField || showModal
                                  }
                                />
                              )}
                          </>

                          {(values.paymentCycle === recurringPayment &&
                            values.invoiceEvery &&
                            values.numberOfBillingCycle) ||
                          values.paymentCycle === oneTimePayment ? (
                            <>
                              <SetTotalSessionDuration />
                              <InputField
                                label='Total Duration Of Contract'
                                labelProps={labelProps}
                                name='totalDurationOfContract'
                                type={
                                  values.paymentCycle === oneTimePayment
                                    ? 'number'
                                    : 'text'
                                }
                                size='lg'
                                placeholder='Indefinite'
                                sm='10'
                                disabled={
                                  !(
                                    values.sowStatus === sowStatus.DRAFT &&
                                    values.paymentCycle === oneTimePayment
                                  )
                                }
                              />
                            </>
                          ) : null}
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                )}
                <Accordion defaultActiveKey={'3'} key={'3'}>
                  <Card className='mb-3'>
                    <Accordion.Toggle
                      as={Card.Header}
                      eventKey={'3'}
                      className={classnames(
                        'border-0 cursor-pointer font-18 font-semibold px-3',
                      )}>
                      Coaching
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey={'3'}>
                      <Card.Body className='px-3 d-flex flex-column'>
                        <Row>
                          <Col lg={2} sm={12} className='pr-1'>
                            <SingleInputLabel
                              isDiscardWidth={isLGScreen ? false : true}
                              label='Number of Sessions'
                              divClasses='justify-content-end'
                              labelClasses={!isLGScreen ? 'h-auto' : ''}
                            />
                          </Col>
                          <Col
                            lg={10}
                            sm={12}
                            className={classnames({
                              'px-075': !isLGScreen,
                              'pl-2': isLGScreen,
                            })}>
                            <Row className='pl-1'>
                              <Col lg={5} md={4} sm={12} className='pl-2'>
                                <InputField
                                  name='sessionPerCoachingCycle'
                                  type='number'
                                  size='lg'
                                  placeholder='Number of sessions'
                                  sm='3'
                                  disabled={
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField || showModal
                                  }
                                />
                              </Col>
                              <Col lg={1} md={1} sm={12}>
                                <SingleInputLabel
                                  isDiscardWidth={isLGScreen ? false : true}
                                  label='Per'
                                  divClasses={classnames(
                                    'justify-content-center',
                                    {
                                      'px-1 mt-2': !isLGScreen,
                                    },
                                  )}
                                />
                              </Col>
                              <Col lg={4} md={4} sm={12}>
                                <SelectionField
                                  labelProps={labelProps}
                                  name='numberOfCoachingCycle'
                                  size='lg'
                                  type='number'
                                  options={SOWBillEvery.map((e) => ({
                                    key: e,
                                    value: e,
                                  }))}
                                  getKeyAsOption={true}
                                  disabled={
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField || showModal
                                  }
                                  optionplaceholder={
                                    values.typeOfCoachingCycle === 'Weekly'
                                      ? 'Number of Weeks'
                                      : values.typeOfCoachingCycle === 'Yearly'
                                      ? 'Number of Years'
                                      : 'Number of Months'
                                  }
                                />
                              </Col>

                              {/* <Col lg={2}>
                                {' '}
                                <SingleInputLabel
                                  isDiscardWidth={isLGScreen ? false : true}
                                  label='Coaching Cycle'
                                  divClasses='justify-content-end'
                                  labelClasses={!isLGScreen ? 'h-auto' : ''}
                                />
                              </Col> */}
                              <Col lg={2}>
                                {' '}
                                <SelectionField
                                  name='typeOfCoachingCycle'
                                  size='lg'
                                  options={[
                                    { label: 'Week', value: 'Weekly' },
                                    { label: 'Month', value: 'Monthly' },
                                    { label: 'Year', value: 'Yearly' },
                                  ]}
                                  getValueAsOption={true}
                                  optionplaceholder='Select Rate Type'
                                  sm='10'
                                  isUseFormikSelectionField={true}
                                  disabled={
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField || showModal
                                  }
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                        {((values.numberOfBillingCycle !== '' &&
                          values.totalDurationOfContract &&
                          values.totalDurationOfContract !== 'Indefinite') ||
                          (values.paymentCycle === oneTimePayment &&
                            values.totalDurationOfContract &&
                            values.totalDurationOfContract !==
                              'Indefinite')) && (
                          <Row className='d-flex'>
                            {' '}
                            <Col lg={2} sm={12} className='pr-1'>
                              <SingleInputLabel
                                isDiscardWidth={isLGScreen ? false : true}
                                label='Total Session'
                                divClasses='justify-content-end'
                                labelClasses={!isLGScreen ? 'h-auto' : ''}
                              />
                            </Col>
                            <Col lg={10} sm={12}>
                              <Row>
                                <Col lg={5} sm={5} className='pl-2'>
                                  <SetTotalSession />
                                  <InputField
                                    // label='Total Session'
                                    labelProps={labelProps}
                                    name='totalSession'
                                    type='number'
                                    size='lg'
                                    placeholder='Total session'
                                    sm='10'
                                    min={1}
                                    disabled={
                                      isSowStatusDraft || coachingPlanWIP
                                        ? viewOnlyField && !user?.isAdmin
                                        : viewOnlyField || showModal
                                    }
                                  />
                                </Col>
                                <Col lg={7} sm={12} className='d-flex pl-lg-4'>
                                  <SingleInputLabel
                                    label='Suggested Total Sessions:'
                                    isDiscardWidth={true}
                                    divClasses={classnames(
                                      'justify-content-end',
                                    )}
                                    labelClasses='pb-9px text-nowrap'
                                  />
                                  <SingleInputLabel
                                    label={
                                      values.sugTotalSession
                                        ? values.sugTotalSession
                                        : '-'
                                    }
                                    isDiscardWidth={true}
                                    divClasses={classnames(
                                      'justify-content-end pl-2',
                                    )}
                                    labelClasses='pb-9px'
                                  />
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        )}
                        <Row className='d-flex mt-2'>
                          <Col lg={2} md={0} sm={0}></Col>
                          <Col lg={10} md={12} sm={12}>
                            <Row>
                              <Col
                                lg={6}
                                md={6}
                                sm={12}
                                className='mb-2 mb-md-0'>
                                <div className='d-flex'>
                                  <Checkbox
                                    name='isConditionalSchedulingEnabled'
                                    type='checkbox'
                                    label='Conditional Scheduling'
                                    disabled={
                                      (isSowStatusDraft || coachingPlanWIP
                                        ? viewOnlyField && !user?.isAdmin
                                        : viewOnlyField || showModal) ||
                                      !values.totalSession ||
                                      Number(values.totalSession) > 3 ||
                                      values.sowType === 'QUEST' ||
                                      values.isPlatformScheduling === false ||
                                      values.isPlatformScheduling === 'false'
                                    }
                                    title={
                                      values.isPlatformScheduling === false ||
                                      values.isPlatformScheduling === 'false'
                                        ? 'Conditional Scheduling is only available with Platform Scheduling'
                                        : values.sowType === 'QUEST'
                                        ? 'Conditional Scheduling is always enabled for Experience type SOW'
                                        : !values.totalSession ||
                                          Number(values.totalSession) > 3
                                        ? 'Conditional Scheduling is only available for 3 or fewer sessions'
                                        : ''
                                    }
                                  />
                                  {(values.isPlatformScheduling === false ||
                                    values.isPlatformScheduling ===
                                      'false') && (
                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip className='font-open-sans font-16 font-semibold leading-7 text-center header-tooltip inviteonly-content-text modal-tooltip'>
                                          Conditional Scheduling is only
                                          available with Platform Scheduling.
                                        </Tooltip>
                                      }>
                                      <span className='ml-2'>
                                        <InformationIcon iconClasses='align-self-center' />
                                      </span>
                                    </OverlayTrigger>
                                  )}
                                </div>
                              </Col>
                              <Col lg={6} md={6} sm={12}>
                                <Checkbox
                                  name='includeGrowthSession'
                                  type='checkbox'
                                  label='Includes Growth Plan?'
                                  disabled={
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField || showModal
                                  }
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg={2} md={0} sm={0} className='pr-1'>
                            <SingleInputLabel
                              isDiscardWidth={isLGScreen ? false : true}
                              label='Regular Session Duration'
                              divClasses='justify-content-end'
                              labelClasses={!isLGScreen ? 'h-auto' : ''}
                            />
                          </Col>
                          <Col lg={10}>
                            <Row>
                              <Col
                                lg={values?.includeGrowthSession ? 5 : 5}
                                md={12}
                                className='pl-2'
                                sm={12}>
                                <InputField
                                  label=''
                                  labelProps={labelProps}
                                  name='sessionDuration'
                                  type='number'
                                  size='lg'
                                  placeholder='Duration In Minutes'
                                  sm='10'
                                  disabled={
                                    isSowStatusDraft || coachingPlanWIP
                                      ? viewOnlyField && !user?.isAdmin
                                      : viewOnlyField || showModal
                                  }
                                />
                              </Col>
                              {values?.includeGrowthSession && (
                                <>
                                  <Col lg={2} md={0} sm={0} className='pr-1'>
                                    <SingleInputLabel
                                      isDiscardWidth={isLGScreen ? false : true}
                                      label='Growth Session Duration'
                                      divClasses='justify-content-end'
                                      labelClasses={!isLGScreen ? 'h-auto' : ''}
                                    />
                                  </Col>
                                  <Col lg={5} md={12} sm={12}>
                                    <InputField
                                      labelProps={labelProps}
                                      name='growthSessionDuration'
                                      type='number'
                                      size='lg'
                                      placeholder='Duration In Minutes'
                                      sm='10'
                                      list='duration-options'
                                      disabled={
                                        isSowStatusDraft || coachingPlanWIP
                                          ? viewOnlyField && !user?.isAdmin
                                          : viewOnlyField || showModal
                                      }
                                    />
                                    <datalist id='duration-options'>
                                      <option value='45'>
                                        45 Minutes Session Duration
                                      </option>
                                      <option value='60'>
                                        60 Minutes Session Duration
                                      </option>
                                    </datalist>
                                  </Col>
                                </>
                              )}
                            </Row>
                          </Col>
                        </Row>

                        {/* <Row>
                          <Col lg={2} md={0} sm={0}></Col>
                          <Col lg={7} md={12} sm={12}>
                            <Checkbox
                              name='report360'
                              type='checkbox'
                              label='360 Report'
                              disabled={
                                isSowStatusDraft || coachingPlanWIP
                                  ? viewOnlyField && !user?.isAdmin
                                  : viewOnlyField || showModal
                              }
                            />
                          </Col>
                        </Row> */}
                        {/* {values.report360 && (
                          <>
                            <SelectionField
                              label={values.report360 && '360 REPORT PLAN'}
                              labelProps={labelProps}
                              name='reportPlan360'
                              options={Labels.ReportPlanOf360}
                              className={classnames({
                                'd-none m-0': !values.report360,
                                'bg-white': isBulkAssignSOWSection,
                              })}
                              disabled={
                                isSowStatusDraft || coachingPlanWIP
                                  ? viewOnlyField && !user?.isAdmin
                                  : viewOnlyField || showModal
                              }
                              size='lg'
                              getKeyAsOption={true}
                            />
                          </>
                        )} */}
                        {/* <Row>
                          <Col lg={2} md={0} sm={0}></Col>
                          <Col lg={7} md={12} sm={12}>
                            <Checkbox
                              name='impactReport'
                              type='checkbox'
                              label='Impact Report'
                              disabled={
                                isSowStatusDraft || coachingPlanWIP
                                  ? viewOnlyField && !user?.isAdmin
                                  : viewOnlyField || showModal
                              }
                            />
                          </Col>
                        </Row> */}
                        {/* {values.impactReport && (
                          <>
                            <SelectionField
                              label={
                                values.impactReport && 'IMPACT REPORT PLAN'
                              }
                              labelProps={labelProps}
                              name='impactReportPlan'
                              options={Labels.impactReportPlan}
                              className={classnames({
                                'd-none m-0': !values.impactReport,
                                'bg-white': isBulkAssignSOWSection,
                              })}
                              disabled={
                                isSowStatusDraft || coachingPlanWIP
                                  ? viewOnlyField && !user?.isAdmin
                                  : viewOnlyField || showModal
                              }
                              size='lg'
                              getKeyAsOption={true}
                            />
                          </>
                        )} */}
                        {values?.includeGrowthSession && (
                          <>
                            {/* COACH RATE - Regular and Growth side-by-side */}
                            {(user.isAdmin ||
                              !viewOnlyField ||
                              (!user.isAdmin &&
                                user?._id === values?.sowCoachId)) && (
                              <Row>
                                <Col lg={2} md={0} sm={0} className='pr-1'>
                                  <SingleInputLabel
                                    isDiscardWidth={isLGScreen ? false : true}
                                    label='REGULAR SESSION COACH RATE (USD)'
                                    divClasses='justify-content-end'
                                    labelClasses={!isLGScreen ? 'h-auto' : ''}
                                    dataTooltip='- Tier 1 - $375 / hour</h1>(Corporation paying for Executive)<br/>- Tier 2 - $250 / hour (Senior Exec Self-Paying)<br/>- Tier 3 - $175 / hour (Emerging Leader / Manager / High potential)<br/>- Custom - Social impact discounts or custom fees as negotiated with client (should be 50% of top line hourly rate)'
                                    showTooltipMessage={true}
                                    extraIcon={
                                      <svg
                                        aria-hidden='true'
                                        focusable='false'
                                        data-prefix='fas'
                                        data-icon='question-circle'
                                        className='svg-inline--fa fa-question-circle fa-w-16'
                                        role='img'
                                        xmlns='http://www.w3.org/2000/svg'
                                        viewBox='0 0 512 512'
                                        width='20'
                                        height='20'>
                                        <path
                                          fill='currentColor'
                                          d='M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zM262.655 90c-54.497 0-89.255 22.957-116.549 63.758-3.536 5.286-2.353 12.415 2.715 16.258l34.699 26.31c5.205 3.947 12.621 3.008 16.665-2.122 17.864-22.658 30.113-35.797 57.303-35.797 20.429 0 45.698 13.148 45.698 32.958 0 14.976-12.363 22.667-32.534 33.976C247.128 238.528 216 254.941 216 296v4c0 6.627 5.373 12 12 12h56c6.627 0 12-5.373 12-12v-1.333c0-28.462 83.186-29.647 83.186-106.667 0-58.002-60.165-102-116.531-102zM256 338c-25.365 0-46 20.635-46 46 0 25.364 20.635 46 46 46s46-20.636 46-46c0-25.365-20.635-46-46-46z'></path>
                                      </svg>
                                    }
                                  />
                                </Col>
                                <Col lg={10}>
                                  <Row>
                                    <Col
                                      lg={5}
                                      md={12}
                                      sm={12}
                                      className='pl-2'>
                                      <InputField
                                        name='coachingRate'
                                        type='number'
                                        size='lg'
                                        placeholder={'Rate'}
                                        sm='10'
                                        list='hourly-rate'
                                        disabled={
                                          isSowStatusDraft || coachingPlanWIP
                                            ? viewOnlyField && !user?.isAdmin
                                            : viewOnlyField || showModal
                                        }
                                      />
                                    </Col>
                                    <Col lg={2} md={0} sm={0} className='pr-1'>
                                      <SingleInputLabel
                                        isDiscardWidth={
                                          isLGScreen ? false : true
                                        }
                                        label='GROWTH SESSION COACH RATE (USD)'
                                        divClasses='justify-content-end'
                                        labelClasses={
                                          !isLGScreen ? 'h-auto' : ''
                                        }
                                      />
                                    </Col>
                                    <Col lg={5} md={12} sm={12}>
                                      <InputField
                                        name='growthCoachingRate'
                                        type='number'
                                        size='lg'
                                        placeholder={'Rate'}
                                        sm='10'
                                        list='hourly-rate'
                                        disabled={
                                          isSowStatusDraft || coachingPlanWIP
                                            ? viewOnlyField && !user?.isAdmin
                                            : viewOnlyField || showModal
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                            )}
                            {/* RATE TYPE - Regular and Growth side-by-side */}
                            <Row>
                              <Col lg={2} md={0} sm={0} className='pr-1'>
                                <SingleInputLabel
                                  isDiscardWidth={isLGScreen ? false : true}
                                  label='REGULAR SESSION RATE TYPE'
                                  divClasses='justify-content-end'
                                  labelClasses={!isLGScreen ? 'h-auto' : ''}
                                />
                              </Col>
                              <Col lg={10}>
                                <Row>
                                  <Col lg={5} md={12} sm={12} className='pl-2'>
                                    <SelectionField
                                      name='coachingRateType'
                                      size='lg'
                                      options={[
                                        { label: 'Hourly', value: 'hourly' },
                                        { label: 'Session', value: 'session' },
                                      ]}
                                      getValueAsOption={true}
                                      optionplaceholder='Select Rate Type'
                                      disabled={
                                        isSowStatusDraft || coachingPlanWIP
                                          ? viewOnlyField && !user?.isAdmin
                                          : viewOnlyField || showModal
                                      }
                                      sm='10'
                                      isUseFormikSelectionField={true}
                                    />
                                  </Col>
                                  <Col lg={2} md={0} sm={0} className='pr-1'>
                                    <SingleInputLabel
                                      isDiscardWidth={isLGScreen ? false : true}
                                      label='GROWTH SESSION RATE TYPE'
                                      divClasses='justify-content-end'
                                      labelClasses={!isLGScreen ? 'h-auto' : ''}
                                    />
                                  </Col>
                                  <Col lg={5} md={12} sm={12}>
                                    <SelectionField
                                      name='growthCoachingRateType'
                                      size='lg'
                                      options={[
                                        { label: 'Hourly', value: 'hourly' },
                                        { label: 'Session', value: 'session' },
                                      ]}
                                      getValueAsOption={true}
                                      optionplaceholder='Select Rate Type'
                                      disabled={
                                        isSowStatusDraft || coachingPlanWIP
                                          ? viewOnlyField && !user?.isAdmin
                                          : viewOnlyField || showModal
                                      }
                                      sm='10'
                                      isUseFormikSelectionField={true}
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </>
                        )}
                        <datalist id='hourly-rate'>
                          <option value='175'>
                            $175 - Emerging Leader (Tier 3)
                          </option>
                          <option value='250'>
                            $250 - Executive Leader (Tier 2)
                          </option>
                          <option value='375'>
                            $375 - Senior/Corporate Executive Leader (Tier 1)
                          </option>
                        </datalist>
                        {!values?.includeGrowthSession && (
                          <>
                            {/* HOURLY RATE PAID TO COACH (USD) - Only when Growth Plan is NOT included */}
                            {(user.isAdmin ||
                              !viewOnlyField ||
                              (!user.isAdmin &&
                                user?._id === values?.sowCoachId)) && (
                              <Row>
                                <Col lg={2} md={0} sm={0} className='pr-1'>
                                  <SingleInputLabel
                                    isDiscardWidth={isLGScreen ? false : true}
                                    label='REGULAR SESSION COACH RATE (USD)'
                                    divClasses='justify-content-end'
                                    labelClasses={!isLGScreen ? 'h-auto' : ''}
                                    dataTooltip='- Tier 1 - $375 / hour</h1>(Corporation paying for Executive)<br/>- Tier 2 - $250 / hour (Senior Exec Self-Paying)<br/>- Tier 3 - $175 / hour (Emerging Leader / Manager / High potential)<br/>- Custom - Social impact discounts or custom fees as negotiated with client (should be 50% of top line hourly rate)'
                                    showTooltipMessage={true}
                                    extraIcon={
                                      <svg
                                        aria-hidden='true'
                                        focusable='false'
                                        data-prefix='fas'
                                        data-icon='question-circle'
                                        className='svg-inline--fa fa-question-circle fa-w-16'
                                        role='img'
                                        xmlns='http://www.w3.org/2000/svg'
                                        viewBox='0 0 512 512'
                                        width='20'
                                        height='20'>
                                        <path
                                          fill='currentColor'
                                          d='M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zM262.655 90c-54.497 0-89.255 22.957-116.549 63.758-3.536 5.286-2.353 12.415 2.715 16.258l34.699 26.31c5.205 3.947 12.621 3.008 16.665-2.122 17.864-22.658 30.113-35.797 57.303-35.797 20.429 0 45.698 13.148 45.698 32.958 0 14.976-12.363 22.667-32.534 33.976C247.128 238.528 216 254.941 216 296v4c0 6.627 5.373 12 12 12h56c6.627 0 12-5.373 12-12v-1.333c0-28.462 83.186-29.647 83.186-106.667 0-58.002-60.165-102-116.531-102zM256 338c-25.365 0-46 20.635-46 46 0 25.364 20.635 46 46 46s46-20.636 46-46c0-25.365-20.635-46-46-46z'></path>
                                      </svg>
                                    }
                                  />
                                </Col>
                                <Col lg={10}>
                                  <Row>
                                    <Col
                                      lg={5}
                                      md={12}
                                      sm={12}
                                      className='pl-2'>
                                      <InputField
                                        name='coachingRate'
                                        type='number'
                                        size='lg'
                                        placeholder={'Rate'}
                                        sm='10'
                                        list='hourly-rate'
                                        disabled={
                                          isSowStatusDraft || coachingPlanWIP
                                            ? viewOnlyField && !user?.isAdmin
                                            : viewOnlyField || showModal
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                            )}
                            {/* REGULAR SESSION RATE TYPE - Only when Growth Plan is NOT included */}
                            <Row>
                              <Col lg={2} md={0} sm={0} className='pr-1'>
                                <SingleInputLabel
                                  isDiscardWidth={isLGScreen ? false : true}
                                  label='REGULAR SESSION RATE TYPE'
                                  divClasses='justify-content-end'
                                  labelClasses={!isLGScreen ? 'h-auto' : ''}
                                />
                              </Col>
                              <Col lg={10}>
                                <Row>
                                  <Col lg={5} md={12} sm={12} className='pl-2'>
                                    <SelectionField
                                      name='coachingRateType'
                                      size='lg'
                                      options={[
                                        { label: 'Hourly', value: 'hourly' },
                                        { label: 'Session', value: 'session' },
                                      ]}
                                      getValueAsOption={true}
                                      optionplaceholder='Select Rate Type'
                                      disabled={
                                        isSowStatusDraft || coachingPlanWIP
                                          ? viewOnlyField && !user?.isAdmin
                                          : viewOnlyField || showModal
                                      }
                                      sm='10'
                                      isUseFormikSelectionField={true}
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                            {/* CLIENT TIME ZONE - shown when Growth Plan is NOT included */}
                            <SelectionField
                              label='CLIENT TIME ZONE'
                              optionplaceholder='Select Time Zone'
                              labelProps={labelProps}
                              name='clientTimeZone'
                              options={Labels.timeZoneList.map((timezone) => ({
                                value: timezone.value,
                                label: `${timezone.label} - ${timezone.value}`,
                              }))}
                              disabled={
                                isSowStatusDraft || coachingPlanWIP
                                  ? viewOnlyField && !user?.isAdmin
                                  : viewOnlyField || showModal
                              }
                              getValueAsOption={true}
                            />
                          </>
                        )}
                        {/* CLIENT TIME ZONE - shown when Growth Plan IS included */}
                        {values?.includeGrowthSession && (
                          <SelectionField
                            label='CLIENT TIME ZONE'
                            optionplaceholder='Select Time Zone'
                            labelProps={labelProps}
                            name='clientTimeZone'
                            options={Labels.timeZoneList.map((timezone) => ({
                              value: timezone.value,
                              label: `${timezone.label} - ${timezone.value}`,
                            }))}
                            disabled={
                              isSowStatusDraft || coachingPlanWIP
                                ? viewOnlyField && !user?.isAdmin
                                : viewOnlyField || showModal
                            }
                            getValueAsOption={true}
                          />
                        )}
                        {/* Plan Starts On */}
                        <SingleDatePicker
                          label='Plan Starts On'
                          labelProps={labelProps}
                          name='planStartDate'
                          sm='10'
                          className='pointer-cursor paid-session-input'
                          disabled={
                            isSowStatusDraft || coachingPlanWIP
                              ? viewOnlyField && !user?.isAdmin
                              : viewOnlyField || showModal
                          }
                          minimumDate={getDateWithOffset(1, 'years', true)}
                          position='up'
                          maximumDate={getDateWithOffset(1, 'years')}
                          {...(isBulkAssignSOWSection && {
                            pickerClass: 'assign-sow',
                          })}
                        />
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                </Accordion>
                <Accordion defaultActiveKey={'4'} key={'4'}>
                  <Card>
                    <Accordion.Toggle
                      as={Card.Header}
                      eventKey={'4'}
                      className={classnames(
                        'border-0 font-18 font-semibold px-3',
                        {
                          'cursor-pointer':
                            values.isConditionalSchedulingEnabled &&
                            values.totalSession &&
                            Number(values.totalSession) <= 3,
                          'cursor-disabled':
                            !values.isConditionalSchedulingEnabled ||
                            !values.totalSession ||
                            Number(values.totalSession) > 3,
                        },
                      )}
                      disabled={
                        !values.isConditionalSchedulingEnabled ||
                        !values.totalSession ||
                        Number(values.totalSession) > 3 ||
                        (isSowStatusDraft || coachingPlanWIP
                          ? viewOnlyField && !user?.isAdmin
                          : viewOnlyField || showModal)
                      }
                      title={
                        !values.isConditionalSchedulingEnabled
                          ? 'Please enable Conditional Scheduling checkbox to use this feature'
                          : !values.totalSession ||
                            Number(values.totalSession) > 3
                          ? 'Conditional Scheduling is only available for plans with 3 or fewer sessions'
                          : ''
                      }>
                      Scheduling
                    </Accordion.Toggle>
                    <Accordion.Collapse
                      eventKey={'4'}
                      disabled={
                        !values.isConditionalSchedulingEnabled ||
                        !values.totalSession ||
                        Number(values.totalSession) > 3
                      }>
                      <Card.Body className='px-3 d-flex flex-column'>
                        {values.isConditionalSchedulingEnabled &&
                        values.totalSession &&
                        Number(values.totalSession) <= 3 ? (
                          Array.from(
                            { length: Number(values.totalSession) || 0 },
                            (_, index) => {
                              const sessionIndex = index + 1
                              return (
                                <SchedulingConditionRow
                                  key={sessionIndex}
                                  sessionIndex={sessionIndex}
                                  values={values}
                                  setFieldValue={setFieldValue}
                                  setFieldTouched={setFieldTouched}
                                  errors={errors}
                                  touched={touched}
                                  isSowStatusDraft={isSowStatusDraft}
                                  coachingPlanWIP={coachingPlanWIP}
                                  viewOnlyField={viewOnlyField}
                                  user={user}
                                  showModal={showModal}
                                  labelProps={labelProps}
                                />
                              )
                            },
                          )
                        ) : (
                          <div className='text-muted text-center py-3'>
                            {!values.isConditionalSchedulingEnabled
                              ? 'Please enable Conditional Scheduling to configure session scheduling conditions.'
                              : !values.totalSession ||
                                Number(values.totalSession) > 3
                              ? 'Conditional Scheduling is only available for plans with 3 or fewer sessions.'
                              : 'Please configure total sessions to enable scheduling conditions.'}
                          </div>
                        )}
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                </Accordion>
                {(planId === 'create' || isBulkAssignSOWSection) && (
                  <ClearMonthAndReportValue
                    coaches={coaches}
                    // setIsStaffCoachSOW={setIsStaffCoachSOW}
                  />
                )}
                <div className='d-flex justify-content-end'>
                  {values.sowStatus === sowStatus.DRAFT &&
                    values.isPaymentRequired === 'true' &&
                    isNotBulkSOWNorCreate &&
                    !isUpdatePlanLoading && (
                      <div className='px-0 mr-2'>
                        {user?.isAdmin && !showModal && (
                          <div className='text-right mt-3'>
                            <Button
                              isLoading={isUpdatePlanLoading}
                              disabled={isUpdatePlanLoading}
                              onClick={() => {
                                if (values.isPaymentRequired === 'true') {
                                  setShowModal(true)
                                } else {
                                  onSubmit({
                                    ...values,
                                    isReadyForPayment: true,
                                  })
                                }
                              }}
                              className='text-uppercase line-height-normal'
                              variant='saffron'>
                              Send for payment?
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  {!isPlanTerminated && (
                    <div className='px-0'>
                      {user?.isAdmin && !showModal && (
                        <div className='text-right mt-3'>
                          {isBulkAssignSOWSection && (
                            <Button
                              // isLoading={isUpdatePlanLoading}
                              className='mr-3 secondary-popup-button px-3 me-3'
                              variant='secondary-light'
                              onClick={() => onBackToPairing(values)}>
                              Back to Pairing
                            </Button>
                          )}
                          {showEmailPreviewButton && (
                            <SOWEmailPreviewButton
                              disabledReason={emailPreviewDisabledReason}
                              onClick={() => setShowEmailPreview(true)}
                            />
                          )}
                          <Button
                            isLoading={isUpdatePlanLoading}
                            disabled={
                              !isBulkAssignSOWSection &&
                              initialValues?.sowStatus !== sowStatus.DRAFT &&
                              initialValues?.sowStatus !== ''
                            }
                            type='submit'
                            className='text-uppercase line-height-normal'
                            variant={
                              isBulkAssignSOWSection ? 'saffron' : 'gray'
                            }
                            onClick={async () => {
                              try {
                                const formErrors = await validateForm()
                                if (
                                  formErrors &&
                                  Object.keys(formErrors).length > 0
                                ) {
                                  // Mark scheduling conditions as touched if there are errors
                                  if (
                                    formErrors.schedulingConditions &&
                                    values.totalSession
                                  ) {
                                    for (
                                      let i = 0;
                                      i < Number(values.totalSession);
                                      i++
                                    ) {
                                      setFieldTouched(
                                        `schedulingConditions[${i}]`,
                                        true,
                                      )
                                    }

                                    // Show specific notifications for each missing field
                                    if (
                                      Array.isArray(
                                        formErrors.schedulingConditions,
                                      )
                                    ) {
                                      formErrors.schedulingConditions.forEach(
                                        (error, idx) => {
                                          if (
                                            error &&
                                            typeof error === 'object'
                                          ) {
                                            const sessionNum = idx + 1
                                            const sessionLabel =
                                              getConditionalSessionLabel(
                                                sessionNum,
                                                values?.sowType,
                                              )
                                            if (error.condition) {
                                              Notification.showNotification(
                                                `${sessionLabel}: ${error.condition}`,
                                                'danger',
                                              )
                                            }
                                            if (error.primaryDate) {
                                              Notification.showNotification(
                                                `${sessionLabel}: ${error.primaryDate}`,
                                                'danger',
                                              )
                                            }
                                            if (error.secondaryDate) {
                                              Notification.showNotification(
                                                `${sessionLabel}: ${error.secondaryDate}`,
                                                'danger',
                                              )
                                            }
                                          } else if (
                                            error &&
                                            typeof error === 'string'
                                          ) {
                                            Notification.showNotification(
                                              error,
                                              'danger',
                                            )
                                          }
                                        },
                                      )
                                    } else if (
                                      typeof formErrors.schedulingConditions ===
                                      'string'
                                    ) {
                                      Notification.showNotification(
                                        formErrors.schedulingConditions,
                                        'danger',
                                      )
                                    }
                                  }

                                  // Show other validation errors
                                  Object.entries(formErrors).forEach(
                                    ([field, message]) => {
                                      if (
                                        field !== 'schedulingConditions' &&
                                        message &&
                                        typeof message === 'string'
                                      ) {
                                        Notification.showNotification(
                                          message,
                                          'danger',
                                        )
                                      }
                                    },
                                  )
                                } else {
                                  console.log(
                                    'SOW form validation passed, submitting...',
                                  )
                                }
                              } catch (e) {
                                Notification.showNotification(
                                  e.message,
                                  'danger',
                                )
                              }
                            }}>
                            {!initialValues?.sowStatus ? 'CREATE SOW' : 'SAVE'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </RBForm>
              <CoachingPlanDetailsModal
                showModal={showModal}
                setShowModal={setShowModal}
                isLoading={isUpdatePlanLoading}
                handleSubmit={onSubmit}
                values={values}
                // isStaffCoachSOW={isStaffCoachSOW}
              />
              <SOWEmailPreviewModal
                show={showEmailPreview}
                onHide={() => setShowEmailPreview(false)}
                payload={emailPreviewPayload}
                isPaymentRequired={values.isPaymentRequired === 'true'}
                isCoachMissingVideoCallLink={isCoachMissingVideoCallLink}
              />
              <PausingCurrentSOWModal
                showModal={pausingCurrentSOWPopup}
                setShowModal={setPausingCurrentSOWPopup}
                isLoading={isUpdatePlanLoading}
                handleSubmit={() => {
                  onSubmit(values)
                  setPausingCurrentSOWPopup(false)
                }}
              />
            </>
          )
        }}
      </Formik>
    </>
  )
}

export default IndividualSOWForm
